import { Dispatch, SetStateAction, useEffect, useState } from 'react';

// Like useState, but the value survives a navigate-away-and-back (the
// component unmounting and remounting) by round-tripping through
// sessionStorage. Scoped to the tab -- cleared on tab close, not shared
// across tabs -- which is the right lifetime for "what I was looking at",
// as opposed to durable server-side state. Supports the same functional-
// updater form as useState's setter (needed wherever a mutation's onSuccess
// updates state relative to its latest value rather than a closed-over one).
export function useSessionState<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Storage unavailable (private browsing, quota, etc.) -- state still
      // works for this mount, it just won't survive a remount.
    }
  }, [key, state]);

  return [state, setState];
}
